// Placement d'un overlay (menu select, calendrier, popover) ancré à un déclencheur,
// porté des placeurs de Claude Design : `place` de core/primitives.jsx pour les menus et le
// calendrier, `placePop` de edition/IdentiteScreen.jsx pour les popovers, qui ne partagent ni leurs
// constantes ni leur règle de bascule :
//   • position fixed calculée depuis le rect de l'ancre ;
//   • bascule au-dessus si pas assez de place en dessous ;
//   • clamp horizontal aux bords du viewport ;
//   • repositionnement au scroll (capture) / resize ;
//   • fermeture au clic extérieur (ancre + overlay exclus) et à Échap.
// L'overlay réel est rendu via <Teleport to="body"> côté composant.

import { useResizeObserver } from '@vueuse/core';
import { measureStickyInset } from '~/composables/ds/dsStickyInset';

export interface DsOverlayPosition {
  left: number;
  width: number;
  top: number;
  bottom: number;
  openUp: boolean;
  edge?: 'top' | 'bottom' | 'right';
  arrow?: number;
  maxHeight?: number;
  arrowShown?: boolean;
}

export interface DsOverlayOptions {
  widthMode?: 'anchor' | number; // largeur = celle de l'ancre, ou valeur fixe (px)
  gap?: number;
  flipThreshold?: number; // px de place en dessous en-deçà desquels on bascule au-dessus
  pad?: number; // marge mini aux bords du viewport
  // Hauteur attendue du flottant (`viewport` : celle de l'écran) : la fournir bascule le placement sur la règle de `placePop`.
  height?: number | 'viewport';
  // `start` (défaut) = aligné sur le bord gauche de l'ancre, pour un menu qui prolonge son champ.
  // `center` = centré sur l'ancre et fléché, pour un popover de confirmation ou d'édition.
  align?: 'start' | 'center';
  preferLeft?: boolean;
  // Placement supplémentaire à faire au même rythme que celui de l'overlay (ex. la découpe
  // lumineuse de DsConfirmPopover). Appelé à la fin de chaque `update`, y compris sans ancre :
  // le composant n'a donc pas à reposer ses propres écouteurs de défilement, qui faisaient
  // relire le rect de l'ancre deux fois par frame.
  onUpdate?: () => void;
  // Keeps the overlay under the sticky or fixed chrome of the page (topbar, sub-navigation) that the
  // anchor slides beneath when the page scrolls. Left off, the overlay follows the anchor over it.
  clearStickyChrome?: boolean;
  canClose?: () => boolean;
}

export interface DsAnchoredOverlay {
  open: Ref<boolean>;
  anchorRef: Ref<HTMLElement | null>;
  floatRef: Ref<HTMLElement | null>;
  pos: Ref<DsOverlayPosition | null>;
  // Bottom edge of the sticky chrome covering the anchor, 0 when none (always 0 without `clearStickyChrome`).
  inset: Ref<number>;
  // Style de positionnement du flottant (`left/width/top|bottom`), identique pour tous les
  // popovers du DS.
  popStyle: ComputedRef<Record<string, string>>;
  update: () => void;
  toggle: () => void;
  close: () => void;
  // Branche l'overlay sur les props `open` / `anchor` d'un composant de popover : synchronisation
  // dans les deux sens, y compris les fermetures venues du composable (Échap, clic extérieur) et
  // le cas « ouvert sans ancre ». `onDetach` est l'événement de fermeture de l'appelant.
  bindToProps: (
    getOpen: () => boolean | undefined,
    getAnchor: () => HTMLElement | null | undefined,
    onDetach: () => void,
  ) => void;
}

// Sélecteur des overlays DS téléportés dans `body` : menus de select, calendrier de DsDateField,
// popovers de confirmation et d'édition. Ils sont visuellement DANS l'overlay qui les a ouverts mais
// pas dans son sous-arbre DOM — d'où deux besoins symétriques :
//   · le clic extérieur ne doit pas les compter comme extérieurs (sinon choisir une date fermait
//     l'éditeur de crédit et jetait la saisie) ;
//   · le piège de focus ne doit pas les compter comme dehors (sinon Tab ramène de force dans le
//     panneau et le menu ouvert devient inatteignable au clavier).
// Exporté pour que `useDsFocusTrap` partage exactement la même définition : deux listes qui
// divergent, c'est l'un des deux comportements qui casse en silence.
export const DS_STACKED_OVERLAYS = '.gd-cf-overlay, .gd-cf-pop, .gd-ce-pop, .gd-select-menu-portal, .gd-cal';

const VIEWPORT_MARGINS = 20;
const SIDE_GAP = 14;
// Flèche bornée à 22px des coins : au-delà elle sortirait de l'arrondi du popover.
const ARROW_INSET = 22;

// Pile des overlays DS ouverts, dans l'ordre d'ouverture (cf. `onKeydown`).
const openOverlays: symbol[] = [];

export function useDsAnchoredOverlay (options: DsOverlayOptions = {}): DsAnchoredOverlay {
  const {
    widthMode = 'anchor', gap = 4, flipThreshold = 260, pad = 8, align = 'start', height,
    preferLeft = false, onUpdate, canClose, clearStickyChrome = false,
  } = options;

  const id = Symbol('ds-overlay');
  const open = ref(false);
  const anchorRef = ref<HTMLElement | null>(null);
  const floatRef = ref<HTMLElement | null>(null);
  const pos = ref<DsOverlayPosition | null>(null);
  const inset = ref(0);

  function update (): void {
    const anchor = anchorRef.value;
    if (anchor) {
      const r = anchor.getBoundingClientRect();
      inset.value = clearStickyChrome ? measureStickyInset(anchor, r) : 0;
      const vh = window.innerHeight;
      const width = widthMode === 'anchor' ? r.width : widthMode;
      const side = preferLeft ? placeLeft(r, vh, width) : null;
      if (side !== null) {
        pos.value = side;
        onUpdate?.();
        return;
      }
      const clampX = (v: number): number => Math.max(pad, Math.min(v, window.innerWidth - width - pad));
      const centerX = r.left + r.width / 2;
      const left = clampX(align === 'center' ? centerX - width / 2 : r.left);
      const arrow = align === 'center'
        ? Math.max(ARROW_INSET, Math.min(centerX - left, width - ARROW_INSET))
        : undefined;

      pos.value = height === undefined
        ? byFlipThreshold(r, vh, left, width, arrow)
        : byDeclaredHeight(r, vh, left, width, arrow, height === 'viewport' ? vh : height);
    }
    onUpdate?.();
  }

  // Highest `top` a popover may take: under the sticky chrome, plus the usual margin.
  function minTop (): number {
    return inset.value > 0 ? inset.value + pad : pad;
  }

  function edgeOf (openUp: boolean): DsOverlayPosition['edge'] {
    if (align !== 'center') { return undefined; }
    return openUp ? 'bottom' : 'top';
  }

  function byFlipThreshold (
    r: DOMRect, vh: number, left: number, width: number, arrow: number | undefined,
  ): DsOverlayPosition {
    const below = vh - r.bottom;
    const openUp = below < flipThreshold && r.top - inset.value > below;
    const against = r.bottom + gap;
    const top = inset.value > 0 ? Math.max(against, minTop()) : against;
    return {
      left, width, top, bottom: vh - r.top + gap, openUp, edge: edgeOf(openUp), arrow, arrowShown: top === against,
    };
  }

  function byDeclaredHeight (
    r: DOMRect, vh: number, left: number, width: number, arrow: number | undefined, wanted: number,
  ): DsOverlayPosition {
    const maxHeight = Math.min(wanted, vh - VIEWPORT_MARGINS);
    const measured = floatRef.value?.offsetHeight ?? 0;
    const effectiveHeight = Math.min(measured || maxHeight, maxHeight);
    const spaceBelow = vh - r.bottom - gap;
    const openUp = !(spaceBelow >= effectiveHeight || spaceBelow >= r.top - inset.value - gap);
    const against = openUp ? r.top - gap - effectiveHeight : r.bottom + gap;
    const top = Math.max(minTop(), Math.min(against, vh - effectiveHeight - pad));
    return {
      left, width, top, bottom: vh - top - effectiveHeight, openUp, edge: edgeOf(openUp), arrow, maxHeight,
      arrowShown: Math.round(top) === Math.round(against),
    };
  }

  function placeLeft (r: DOMRect, vh: number, width: number): DsOverlayPosition | null {
    const left = r.left - SIDE_GAP - width;
    if (left < pad) { return null; }
    const floatHeight = floatRef.value?.offsetHeight ?? 0;
    const anchorCenter = r.top + r.height / 2;
    const top = floatHeight === 0
      ? Math.max(minTop(), r.top)
      : Math.max(minTop(), Math.min(anchorCenter - floatHeight / 2, vh - floatHeight - pad));
    const arrow = Math.max(ARROW_INSET, Math.min(anchorCenter - top, Math.max(ARROW_INSET + 2, floatHeight - ARROW_INSET)));
    return { left, width, top, bottom: vh - top - floatHeight, openUp: false, edge: 'right', arrow, arrowShown: true };
  }

  const popStyle = computed<Record<string, string>>(() => (pos.value
    ? {
        left: `${pos.value.left}px`,
        width: `${pos.value.width}px`,
        ...(pos.value.maxHeight === undefined
          ? (pos.value.openUp
              ? { top: 'auto', bottom: `${pos.value.bottom}px` }
              : { top: `${pos.value.top}px`, bottom: 'auto' })
          : { top: `${pos.value.top}px`, bottom: 'auto', maxHeight: `${pos.value.maxHeight}px` }),
      }
    : { left: '-9999px', top: '-9999px' }));

  function close (): void {
    if (canClose && !canClose()) { return; }
    open.value = false;
  }
  function toggle (): void { open.value = !open.value; }

  // Overlays DS empilés : un popover ouvert PAR CELUI-CI (confirmation de suppression d'un
  // commentaire, par exemple) est téléporté dans `body`, donc hors de `floatRef` — sans cette
  // garde, cliquer « Supprimer » dans la confirmation fermait le popover qui l'avait ouverte.
  const STACKED_OVERLAYS = DS_STACKED_OVERLAYS;

  // Ne neutraliser que ce qui est réellement AU-DESSUS, dans l'ordre de téléportation dans `body`.
  // Tester la seule appartenance à un conteneur d'overlay rendait infermable tout menu ouvert dans
  // une modale : son voile est l'ancêtre de TOUT ce qu'elle affiche, y compris du clic extérieur.
  function isStackedAbove (target: Element): boolean {
    const float = floatRef.value;
    if (!float) { return false; }
    const stacked = target.closest(STACKED_OVERLAYS);
    if (!stacked || stacked.contains(float)) { return false; }
    return (float.compareDocumentPosition(stacked) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;
  }

  function onDocMouseDown (event: MouseEvent): void {
    const target = event.target as Node;
    if (anchorRef.value?.contains(target)) { return; }
    if (floatRef.value?.contains(target)) { return; }
    if (target instanceof Element && isStackedAbove(target)) { return; }
    close();
  }
  // Échap ne doit fermer QUE l'overlay le plus haut. Deux arrêts sont nécessaires, et un test de
  // position dans la pile :
  //   • `stopPropagation` retient l'événement avant `window`, où écoutent les modales hôtes (sans
  //     lui, une seule frappe fermait le menu ET la modale, emportant la saisie en cours) ;
  //   • `stopImmediatePropagation` retient les écouteurs FRÈRES, tous posés sur `document` par les
  //     autres overlays — `stopPropagation` ne les concerne pas, ils sont sur le même nœud ;
  //   • la pile enfin, parce que l'overlay OUVERT EN PREMIER a enregistré son écouteur en premier
  //     et s'exécute donc avant : aucun arrêt déclenché par l'enfant ne peut le rattraper.
  function onKeydown (event: KeyboardEvent): void {
    if (event.key !== 'Escape') { return; }
    if (openOverlays[openOverlays.length - 1] !== id) { return; }
    event.stopPropagation();
    event.stopImmediatePropagation();
    close();
  }

  function unstack (): void {
    const at = openOverlays.lastIndexOf(id);
    if (at !== -1) { openOverlays.splice(at, 1); }
  }

  if (height !== undefined || preferLeft) { useResizeObserver(floatRef, () => update()); }

  let raf = 0;
  watch(open, (isOpen) => {
    if (!import.meta.client) { return; }
    if (isOpen) {
      openOverlays.push(id);
      raf = requestAnimationFrame(update);
      document.addEventListener('mousedown', onDocMouseDown);
      document.addEventListener('keydown', onKeydown);
      window.addEventListener('scroll', update, true);
      window.addEventListener('resize', update);
    } else {
      // Position OUBLIÉE à la fermeture. Tous les appelants gardent leur flottant sous
      // `v-if="open && pos"` : en conservant l'ancienne position, une réouverture sur une AUTRE
      // ancre passait le `v-if` avant le replacement (différé au tick suivant) et peignait une
      // frame aux coordonnées de l'ancre précédente — une confirmation de suppression désignait
      // la mauvaise ligne.
      pos.value = null;
      inset.value = 0;
      unstack();
      cancelAnimationFrame(raf);
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeydown);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      onUpdate?.();
    }
  });

  // Popovers pilotés par des props (`open` + `anchor`) : ConfirmPopover et EditPopover
  // réécrivaient ce protocole ligne pour ligne, au nom de l'événement près.
  function bindToProps (
    getOpen: () => boolean | undefined,
    getAnchor: () => HTMLElement | null | undefined,
    onDetach: () => void,
  ): void {
    watch(() => [getOpen(), getAnchor()] as const, ([isOpen, el]) => {
      anchorRef.value = el ?? null;
      open.value = Boolean(isOpen && el);
      if (open.value) {
        nextTick(update);
      } else if (isOpen) {
        // Demandé ouvert sans ancre : rien à placer, et `open` ne bouge pas — donc le watch
        // ci-dessous n'émet rien. L'appelant resterait avec un popover ouvert et invisible.
        onDetach();
      }
    }, { immediate: true });

    // `open` se ferme aussi de lui-même (Échap, clic extérieur) : l'appelant doit le savoir, sinon
    // sa propre prop `open` resterait vraie et le popover ne se réouvrirait plus.
    watch(open, (isOpen) => { if (!isOpen && getOpen()) { onDetach(); } });
  }

  onUnmounted(() => {
    if (!import.meta.client) { return; }
    unstack();
    cancelAnimationFrame(raf);
    document.removeEventListener('mousedown', onDocMouseDown);
    document.removeEventListener('keydown', onKeydown);
    window.removeEventListener('scroll', update, true);
    window.removeEventListener('resize', update);
  });

  return { open, anchorRef, floatRef, pos, inset, popStyle, update, toggle, close, bindToProps };
}
